/**
 * =============================================================================
 * PATIENT JOURNEY - WORKFLOW STATE
 * =============================================================================
 *
 * The workflow itself now lives on the backend. This hook is what is left on the
 * client:
 *
 *   - the unsaved draft for the step being answered, and its live validation
 *   - submitting that draft and adopting the state the server sends back
 *
 * It does not decide the next step, whether a step closes the case, or which
 * stages are complete. All of that arrives resolved in the journey payload.
 *
 * The public API is unchanged from the version that ran the state machine here,
 * so the cards, the stepper and the progress bar consume it exactly as before.
 * =============================================================================
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
    NODE_TYPE,
    getDefaultFieldValues,
    getOptionFields,
    isTimestampRequired,
} from "./journeyConfig";

/* -----------------------------------------------------------------------------
   STAGE STATUS - the vocabulary the server uses in progress.stageStatuses
----------------------------------------------------------------------------- */

export const STAGE_STATUS = {
    COMPLETED: "completed",
    ACTIVE: "active",
    PENDING: "pending",
    SKIPPED: "skipped",
};

export const STAGE_STATUS_LABEL = {
    [STAGE_STATUS.COMPLETED]: "Completed",
    [STAGE_STATUS.ACTIVE]: "In Progress",
    [STAGE_STATUS.PENDING]: "Pending",
    [STAGE_STATUS.SKIPPED]: "Not applicable",
};

/* -----------------------------------------------------------------------------
   DRAFT
----------------------------------------------------------------------------- */

const emptyDraft = () => ({
    optionValue: "",
    timestamp: null,
    values: {},
    acknowledged: false,
});

const isValidDate = (value) =>
    value instanceof Date && !Number.isNaN(value.getTime());

/* The earliest time a step may be recorded at - the moment the case was
   referred, floored to the minute so that minute itself stays selectable. */
const toMinTimestamp = (value) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }

    const date =
        value instanceof Date
            ? new Date(value.getTime())
            : new Date(/^\d+$/.test(String(value)) ? Number(value) : value);

    if (!isValidDate(date)) {
        return null;
    }

    date.setSeconds(0, 0);

    return date;
};

const BEFORE_REFERRAL_MESSAGE =
    "You cannot select a date and time before the case was referred.";

const isBeforeReferral = (value, minTimestamp) =>
    Boolean(minTimestamp) &&
    isValidDate(value) &&
    value.getTime() < minTimestamp.getTime();

const findOption = (node, optionValue) =>
    (node?.options || []).find((option) => option.value === optionValue) || null;

/* -----------------------------------------------------------------------------
   LOCAL VALIDATION

   A UX affordance, not an authority. It keeps the action button disabled and the
   "what is still missing" hint live without a round trip. Its rules come from the
   server's node, and the server validates again on submit and wins.
----------------------------------------------------------------------------- */

const validateRequiredFields = (fields, values, errors) => {
    (fields || []).forEach((field) => {
        const value = values?.[field.name];

        if (
            field.required &&
            (value === undefined ||
                value === null ||
                String(value).trim() === "")
        ) {
            errors[field.name] = field.label + " is required.";
        }
    });

    return errors;
};

const validateDraft = (node, draft, minTimestamp = null) => {
    const errors = {};

    if (!node) {
        return errors;
    }

    if (node.type === NODE_TYPE.ACKNOWLEDGMENT) {
        if (!draft.acknowledged) {
            errors.acknowledged =
                "Please confirm you have reviewed the report and the prescription.";
        }

        return errors;
    }

    if (node.type === NODE_TYPE.FORM) {
        return validateRequiredFields(node.fields, draft.values, errors);
    }

    /* OUTCOME */

    const option = findOption(node, draft.optionValue);

    if (!option) {
        errors.optionValue = "Select one outcome to continue.";

        return errors;
    }

    if (option.requiresTimestamp) {
        const isRequired = isTimestampRequired(node, option);

        const isEmpty =
            draft.timestamp === null || draft.timestamp === undefined;

        if (isEmpty) {
            if (isRequired) {
                errors.timestamp = "Enter a valid date and time.";
            }
        } else if (!isValidDate(draft.timestamp)) {
            errors.timestamp = "Enter a valid date and time.";
        } else if (draft.timestamp.getTime() > Date.now()) {
            errors.timestamp = "The time cannot be in the future.";
        } else if (isBeforeReferral(draft.timestamp, minTimestamp)) {
            errors.timestamp = BEFORE_REFERRAL_MESSAGE;
        }
    }

    return validateRequiredFields(
        getOptionFields(option),
        draft.values,
        errors
    );
};

/* -----------------------------------------------------------------------------
   SUBMIT PAYLOAD

   An event time is sent with its offset and its IANA zone. The server rejects a
   bare local time, because that distinction is what makes the recorded clinical
   times comparable between a hub and a spoke.
----------------------------------------------------------------------------- */

const toOffsetISO = (date) => {
    if (!isValidDate(date)) {
        return null;
    }

    const pad = (value) => String(Math.floor(Math.abs(value))).padStart(2, "0");

    const offsetMinutes = -date.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? "+" : "-";

    return (
        date.getFullYear() +
        "-" + pad(date.getMonth() + 1) +
        "-" + pad(date.getDate()) +
        "T" + pad(date.getHours()) +
        ":" + pad(date.getMinutes()) +
        ":" + pad(date.getSeconds()) +
        sign + pad(offsetMinutes / 60) +
        ":" + pad(offsetMinutes % 60)
    );
};

const localTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch (error) {
        return null;
    }
};

/**
 * The locked summary shown on a card the instant a step is saved, before the
 * server answers. Labels come from the descriptors the server already sent, so
 * nothing is being decided here - only assembled.
 */
const provisionalDetails = (node, option, values) => {
    const fields = [...getOptionFields(option), ...(node?.fields || [])];

    return Object.entries(values || {})
        .filter(
            ([, value]) =>
                value !== undefined && value !== null && value !== ""
        )
        .map(([name, value]) => {
            const field = fields.find((item) => item.name === name);

            const choice = (field?.options || []).find(
                (item) => item.value === value
            );

            return {
                name,
                label:
                    field?.label ||
                    name.replace(/([A-Z])/g, " $1").trim(),
                value: choice?.label || String(value),
            };
        });
};

/* -----------------------------------------------------------------------------
   HOOK
----------------------------------------------------------------------------- */

/**
 * @param {object}   params
 * @param {object}   params.journey    the journey payload from the case API
 * @param {boolean}  params.isLoading  the journey is still being fetched
 * @param {string}   params.recordedBy name shown on an optimistically saved step
 * @param {string}   params.referredAt when the case was referred - no step
 *                                     can be timed before it
 * @param {function} params.onSubmit   (reportId, body) => server response
 * @param {function} params.onStepSave called once a step is committed
 * @param {function} params.onError    called with a message the user should see
 */
export default function useCaseWorkflow({
    journey = null,
    isLoading = false,
    recordedBy = "",
    referredAt = null,
    onSubmit,
    onStepSave,
    onError,
} = {}) {
    const [state, setState] = useState(journey);
    const [draft, setDraft] = useState(emptyDraft);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    /* A provisional entry, shown while the server is answering. */
    const [pending, setPending] = useState(null);

    const revisionRef = useRef(-1);

    /* -------------------------------------------------------------------------
       ADOPTING SERVER STATE

       A revalidation that arrives carrying an older revision than the one we
       already hold is stale - a step we just saved must not be undone by it.
    ------------------------------------------------------------------------- */

    useEffect(() => {
        if (!journey) {
            return;
        }

        const incoming = journey.state?.revision ?? 0;

        if (incoming >= revisionRef.current) {
            revisionRef.current = incoming;
            setState(journey);
        }
    }, [journey]);

    const adopt = useCallback((next) => {
        if (!next) {
            return;
        }

        revisionRef.current = next.state?.revision ?? 0;
        setState(next);
        setPending(null);
        setDraft(emptyDraft());
        setErrors({});
    }, []);

    /* -------------------------------------------------------------------------
       DERIVED - read straight off the payload
    ------------------------------------------------------------------------- */

    const currentNode = pending ? null : state?.currentNode || null;

    const currentStage = state?.state?.currentStage ?? 0;

    const isClosed = pending ? false : Boolean(state?.state?.isClosed);

    const entries = useMemo(() => {
        const committed = state?.entries || [];

        return pending ? [...committed, pending] : committed;
    }, [state, pending]);

    const stageStatuses = state?.progress?.stageStatuses || [];

    const upcomingStages = state?.progress?.upcomingStages || [];

    const reachedStage = state?.progress?.reachedStage ?? 0;

    const closingEntry = state?.closingEntry || null;

    const closedAt = state?.state?.closedAt || null;

    /*
     * False only for a case closed without a single step recorded - closed
     * before this workflow existed, or through the plain close endpoint. There
     * is nothing to show and nothing can ever be added, which is not the same
     * as an OPEN case whose journey simply has not been started yet.
     *
     * The backend says so outright; the fallback keeps this correct against a
     * response that predates the flag.
     */
    const hasJourney =
        state?.state?.hasJourney === undefined
            ? !isClosed || entries.length > 0
            : Boolean(state.state.hasJourney);

    /* The report and prescription for this case, signed by the backend. */
    const documents = useMemo(() => state?.documents || [], [state]);

    const definition = state?.definition || null;

    const stages = definition?.stages || [];

    const selectedOption = useMemo(
        () => findOption(currentNode, draft.optionValue),
        [currentNode, draft.optionValue]
    );

    const hasProgress =
        !isClosed &&
        (entries.length > 0 ||
            Boolean(draft.optionValue) ||
            Boolean(draft.acknowledged));

    /**
     * Saving the current selection would end the workflow.
     */
    const willCloseCase = useMemo(() => {
        if (!currentNode) {
            return false;
        }

        if (currentNode.type === NODE_TYPE.FORM) {
            return Boolean(currentNode.terminal);
        }

        return Boolean(selectedOption?.terminal);
    }, [currentNode, selectedOption]);

    const minTimestamp = useMemo(() => toMinTimestamp(referredAt), [referredAt]);

    const pendingErrors = useMemo(
        () =>
            currentNode ? validateDraft(currentNode, draft, minTimestamp) : {},
        [currentNode, draft, minTimestamp]
    );

    const canSubmit =
        Object.keys(pendingErrors).length === 0 &&
        !isClosed &&
        !isSubmitting &&
        Boolean(currentNode) &&
        state?.permissions?.canSubmit !== false;

    const pendingRequirement = useMemo(() => {
        const messages = Object.values(pendingErrors).filter(Boolean);

        return messages.length > 0 ? messages[0] : null;
    }, [pendingErrors]);

    /* -------------------------------------------------------------------------
       DRAFT ACTIONS - unchanged
    ------------------------------------------------------------------------- */

    const selectOption = useCallback(
        (optionValue) => {
            const option = findOption(currentNode, optionValue);

            setDraft((previous) => {
                /* Switching the outcome drops the timestamp and the sub
                   questions of the previous one, so nothing from an abandoned
                   answer is carried over. */
                if (previous.optionValue === optionValue) {
                    return previous;
                }

                return {
                    ...previous,
                    optionValue,
                    timestamp: null,
                    values: getDefaultFieldValues(getOptionFields(option)),
                };
            });

            setErrors({});
        },
        [currentNode]
    );

    /* A time before the referral is flagged as soon as it is picked or typed,
       not only on submit. */
    const setTimestamp = useCallback(
        (value) => {
            setDraft((previous) => ({
                ...previous,
                timestamp: value,
            }));

            setErrors((previous) => ({
                ...previous,
                timestamp: isBeforeReferral(value, minTimestamp)
                    ? BEFORE_REFERRAL_MESSAGE
                    : undefined,
            }));
        },
        [minTimestamp]
    );

    const setFieldValue = useCallback((name, value) => {
        setDraft((previous) => ({
            ...previous,
            values: {
                ...previous.values,
                [name]: value,
            },
        }));

        setErrors((previous) => ({
            ...previous,
            [name]: undefined,
        }));
    }, []);

    const setAcknowledged = useCallback((value) => {
        setDraft((previous) => ({
            ...previous,
            acknowledged: value,
        }));

        setErrors({});
    }, []);

    /* -------------------------------------------------------------------------
       COMMIT

       Optimistic: the answered step locks into the timeline immediately and the
       server's reply replaces it. Six sequential round trips would otherwise be
       six visible waits, on connections that are often poor.
    ------------------------------------------------------------------------- */

    const submitStep = useCallback(
        async ({ confirmed = false } = {}) => {
            if (!currentNode || !state || isSubmitting) {
                return null;
            }

            const validationErrors = validateDraft(
                currentNode,
                draft,
                minTimestamp
            );

            if (Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);

                return null;
            }

            const option = findOption(currentNode, draft.optionValue);

            const capturesTime = Boolean(option?.requiresTimestamp);

            const body = {
                nodeId: currentNode.id,
                revision: state.state?.revision ?? 0,
                confirmed,
                action: {
                    type: currentNode.type,
                    optionValue: option?.value || null,
                    acknowledged:
                        currentNode.type === NODE_TYPE.ACKNOWLEDGMENT
                            ? Boolean(draft.acknowledged)
                            : null,
                    timestamp: capturesTime ? toOffsetISO(draft.timestamp) : null,
                    timezone: capturesTime ? localTimezone() : null,
                    values: { ...draft.values },
                },
            };

            const provisional = {
                entryId: "pending",
                nodeId: currentNode.id,
                stage: currentNode.stage ?? currentStage,
                stepLabel: currentNode.stepLabel,
                title: currentNode.title,
                location: currentNode.location,
                outcomeValue: option?.value || null,
                outcomeLabel: option?.label || null,
                tone: option?.tone || null,
                terminal: Boolean(option?.terminal),
                timestamp: capturesTime ? toOffsetISO(draft.timestamp) : null,
                recordedAt: new Date().toISOString(),
                recordedBy: recordedBy || null,
                details: provisionalDetails(
                    currentNode,
                    option,
                    draft.values
                ),
            };

            setIsSubmitting(true);
            setPending(provisional);
            setErrors({});

            const response = await onSubmit(body);

            setIsSubmitting(false);

            /* Saved. */
            if (response?.ok) {
                adopt(response.data);

                if (typeof onStepSave === "function") {
                    onStepSave(provisional, {
                        journey: response.data,
                        isClosed: Boolean(response.data?.state?.isClosed),
                    });
                }

                return response.data;
            }

            /* Nothing was recorded - roll the optimistic entry back. */
            setPending(null);

            /* The server rejected the values. */
            if (response?.status === 422 && response.errors) {
                setErrors(response.errors);

                return null;
            }

            /* Someone else moved this case on, or it is already closed. The
               server sent the truth with it. */
            if (
                (response?.status === 409 || response?.status === 403) &&
                response.data
            ) {
                adopt(response.data);

                if (typeof onError === "function") {
                    onError(response.message);
                }

                return null;
            }

            /* A midway close needs confirming first. The copy belongs to this
               app, so only the requirement comes back. */
            if (response?.status === 428) {
                return { requiresConfirmation: true };
            }

            if (typeof onError === "function") {
                onError(response?.message || "Could not save this step.");
            }

            return null;
        },
        [
            currentNode,
            currentStage,
            state,
            draft,
            isSubmitting,
            recordedBy,
            onSubmit,
            onStepSave,
            onError,
            adopt,
        ]
    );

    const resetWorkflow = useCallback(() => {
        setDraft(emptyDraft());
        setErrors({});
        setPending(null);
    }, []);

    return {
        /* history */
        entries,
        closingEntry,

        /* case files */
        documents,

        /* current position */
        currentNode,
        currentStage,
        isClosed,
        closedAt,
        hasJourney,
        hasProgress,

        /* progress bar */
        definition,
        stages,
        stageStatuses,
        upcomingStages,
        reachedStage,

        /* draft */
        draft,
        errors,
        minTimestamp,
        selectedOption,
        willCloseCase,
        canSubmit,
        pendingRequirement,

        /* status */
        isLoading,
        isSubmitting,
        isReady: Boolean(state),

        /* actions */
        selectOption,
        setTimestamp,
        setFieldValue,
        setAcknowledged,
        submitStep,
        resetWorkflow,
    };
}
